import React, { useState, useEffect, useRef } from 'react';
import { FilterState, LocationSelection } from '../types';
import { STYLE_OPTIONS, AGE_OPTIONS, EXPERIENCE_OPTIONS } from '../lib/filters';
import { X, MapPin, Navigation, Loader2 } from 'lucide-react';
import { getLocationSuggestions, resolveNearestLocation } from '../services/locationService';
import { useRouteStateSlug } from '../hooks/useRouteStateSlug';
import type { StateSlug } from '../lib/stateCore.mjs';
import { normalizeLocationDisplay } from '../lib/slugs';
import { Button } from '../src/components/ui/button';
import { Input } from '../src/components/ui/input';
import { LabelText } from '../src/components/ui/typography';
import { useFocusTrap } from '../src/hooks/useFocusTrap';
import { toast } from '../src/lib/toast';
import { ModalPortal } from '../src/components/ui/modal-portal';
import LiveAnnouncer from './LiveAnnouncer';

interface FilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialFilters: FilterState;
    /** Canonical "Suburb Postcode" to seed the location input with when available
     *  (e.g. on a Google-entry page whose URL omits the postcode). Falls back to
     *  initialFilters.suburb. */
    locationDisplay?: string;
    /** resolvedDisplay: the canonical "Suburb Postcode" for the applied location when it
     *  is known client-side (a picked suggestion or the unedited seed), so the directory
     *  pill can show the postcode from first paint. Omitted for a free-typed suburb. */
    /** `resolvedState` is the state the LOCATION resolved to (geolocation), which may
     *  differ from the route's state. Absent for a typed suburb — the caller then keeps
     *  using the route's state, which is the historical behaviour. */
    onApply: (filters: FilterState, resolvedDisplay?: string, resolvedState?: StateSlug) => void;
    onClearAll: () => void;
    gymCount?: number;
}

/** Canonical "Suburb Postcode" (e.g. "Alexandria 2015") for a picked location — matches
 *  the backend's resolved form (`${locality} ${postcode}`) so the directory pill can show
 *  the postcode instantly. Empty when the parts are unknown (guard then falls back). */
const toResolvedDisplay = (sel: LocationSelection): string =>
    sel.suburb && sel.postcode ? `${sel.suburb} ${sel.postcode}` : '';

const FilterModal: React.FC<FilterModalProps> = ({ isOpen, onClose, initialFilters, locationDisplay, onApply, onClearAll }) => {
    const [localFilters, setLocalFilters] = useState<FilterState>(initialFilters);
    // R-02: scope suburb autocomplete to the state the current route is on — the
    // callable defaults to NSW suggestions when state is omitted, so a VIC page's
    // filter modal could only ever suggest NSW suburbs. Read through a ref so a
    // cross-state navigation does NOT re-run the debounce effect below — the modal
    // stays mounted while closed with a seeded suburbInput, and a dep on the state
    // fired a suggestion callable on every cross-state navigation (review minor).
    const routeStateSlug = useRouteStateSlug();
    const routeStateSlugRef = useRef(routeStateSlug);
    routeStateSlugRef.current = routeStateSlug;

    // Autocomplete State for Suburb
    const [suburbInput, setSuburbInput] = useState(locationDisplay || initialFilters.suburb);
    /**
     * ⚠️ L2 (operator-ruled 2026-08-20) — HAS THE VISITOR PICKED A REAL SUBURB?
     *
     * `selectedDisplayRef` already tracks this, but a REF CANNOT RE-RENDER, and the
     * Show Results button's enabled state has to update the instant a suggestion is
     * chosen. So this mirrors the ref as state; every write below sits beside the
     * existing ref write rather than replacing it, because the ref carries the resolved
     * POSTCODE (a different job) and the two must not be merged.
     *
     * WHY THIS EXISTS: a free-typed suburb used to be searchable, and that path waits on
     * a network lookup BEFORE any loading state is set — the platform's only unbounded
     * no-feedback window. The operator ruled it removed BY DESIGN rather than covered
     * with a spinner: you may only search a suburb the dropdown offered.
     */
    const [suburbPicked, setSuburbPicked] = useState(false);
    /**
     * ⚠️ L2 (operator-ruled 2026-08-20) — "no suggestions" meant THREE different things
     * and the hint below must not confuse them: still looking, the location index being
     * down, or a genuine no-match. Without this flag the no-match line would flash after
     * nearly every keystroke, during the debounce, telling people their spelling was
     * wrong while the answer was still in flight.
     */
    const [isSearchingSuburbs, setIsSearchingSuburbs] = useState(false);

    /**
     * ⚠️⚠️ UI-130 (loading spec RULED B1 / B2) — TWO PENDING FLAGS, AND BOTH MUST BE
     * RESET ON OPEN. Read this before copying the hero's pattern into here.
     *
     * `pages/LandingPage.tsx` sets an identical flag and NEVER clears it, which is correct
     * there because that page is a plain `<Route element>` and unmounts on navigation.
     * **THIS COMPONENT DOES NOT UNMOUNT.** DirectoryPage renders it unconditionally and it
     * returns `null` at `if (!isOpen)` further down — so every piece of state here SURVIVES
     * a close. A never-cleared `isApplyPending` would disable Show Results permanently
     * from the second opening onward. They are re-armed in the open-transition effect
     * below, which already exists to seed the draft.
     */
    const [isApplyPending, setIsApplyPending] = useState(false);
    const [isLocating, setIsLocating] = useState(false);
    const [suburbSuggestions, setSuburbSuggestions] = useState<LocationSelection[]>([]);
    const [showSuburbSuggestions, setShowSuburbSuggestions] = useState(false);
    const [indexUnavailable, setIndexUnavailable] = useState(false);
    const suburbWrapperRef = useRef<HTMLDivElement>(null);
    const suburbListRef = useRef<HTMLUListElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    // Keyboard Navigation State
    const [suburbActiveIndex, setSuburbActiveIndex] = useState(-1);

    // Canonical "Suburb Postcode" for the last picked suggestion, so Apply can hand the
    // parent an optimistic display for the pill (no wait for the search round-trip).
    // Cleared whenever the user edits the field — a free-typed suburb has no known postcode.
    const selectedDisplayRef = useRef<string>('');
    /**
     * UI-121 — THE SUBURB THAT `localFilters.location_entry_id` ACTUALLY IDENTIFIES.
     *
     * ⚠️ THE DEFECT THIS EXISTS FOR: `location_entry_id` is a hidden handle on a specific
     * locality entry, and `useDirectoryFilters.getCanonicalSearchKey` treats it as
     * EXCLUSIVE — when an entryId is present the suburb TEXT is dropped from the search
     * key entirely. So a stale id does not merely mis-target; it makes a suburb change
     * INVISIBLE to the dedupe check, and the search never runs at all.
     *
     * Only three handlers ever set a real id (the two suggestion picks and geolocation).
     * The two handlers that change the suburb WITHOUT picking used to leave the id
     * untouched, so after one dropdown pick every later suburb change in the same panel
     * session searched the FIRST suburb — silently when only the suburb moved, and with a
     * full loading ceremony returning the wrong suburb's gyms when the radius moved too.
     *
     * ⚠️ THIS REF IS THE CLASS FIX, NOT THE INSTANCE FIX (docs/guard-scope-convention.md).
     * Clearing the id inside the two offending handlers would close the two paths that
     * exist TODAY; pairing the id with its owning suburb and re-checking at the single
     * apply choke point closes the path a FOURTH handler would otherwise re-open.
     * ⭐ Its failure direction is safe by construction: a wrong verdict drops an id and
     * costs one extra lookup round-trip. It can degrade a search, never mis-target one.
     *
     * ⚠️ SEEDED AT MOUNT AS WELL AS ON OPEN, and the mount half is not belt-and-braces.
     * The open effect below fires only on the closed→open TRANSITION, so a modal whose
     * very first render is already open never runs it. `DirectoryPage` mounts this
     * component with `isOpen={false}` and toggles, so today the transition always fires —
     * but `useState(initialFilters)` already seeds the draft filters at mount, and a ref
     * that did not seed alongside it would silently disagree with them for exactly the
     * renders that skip the effect. Found by this fix's own test, not by reading.
     */
    const entryIdSuburbRef = useRef<string>(initialFilters.suburb);
    // ITEM 6 SLICE 1 (operator-ruled 2026-08-12) — the state the LOCATION resolved to,
    // which is not always the state the PAGE is on. `resolveNearestLocation` has
    // returned this since UI-28 and handleUseLocation threw it away, so Apply built the
    // URL from the route's state instead: a Victorian using "use my location" on the
    // state-less directory got their Victorian suburb NAME inside an NSW address.
    // Measured over the real corpus: 19 of 388 VIC gym-bearing suburbs land on a REAL
    // prerendered NSW page (a plausible wrong-city result), 369 on a dead route.
    // Cleared whenever the field is edited — a typed suburb has no resolved state.
    const resolvedStateRef = useRef<StateSlug | undefined>(undefined);

    // Sync state ONLY on the closed→open transition (D8). Running on every
    // initialFilters change while open would clobber an in-progress draft (e.g.
    // the parent re-syncing applied filters mid-edit). prevIsOpen gates it to the
    // moment the modal opens, seeding the draft from the latest parent filters.
    const prevIsOpenRef = useRef(isOpen);
    useEffect(() => {
        if (isOpen && !prevIsOpenRef.current) {
            setLocalFilters(initialFilters);
            setSuburbInput(locationDisplay || initialFilters.suburb);
            // L2: the seed is the suburb the visitor is ALREADY on, so it needs no
            // re-picking — `suburbMatchesSeed` below treats it as valid without this
            // flag, and resetting here keeps "picked" meaning "chosen this session".
            setSuburbPicked(false);
            // UI-121: the seeded id belongs to the seeded suburb. Seeding the pair
            // together is what makes the unedited-seed apply keep its id (and skip a
            // redundant lookup) while any EDIT away from it invalidates the id.
            entryIdSuburbRef.current = initialFilters.suburb;
            // UI-130: re-arm both acknowledgement flags. This component never unmounts
            // (see their declaration), so without this a single apply would leave Show
            // Results disabled for the rest of the session.
            setIsApplyPending(false);
            setIsLocating(false);
        }
        prevIsOpenRef.current = isOpen;
    }, [isOpen, initialFilters, locationDisplay]);

    // Click outside handler for dropdowns
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (suburbWrapperRef.current && !suburbWrapperRef.current.contains(event.target as Node)) {
                setShowSuburbSuggestions(false);
            }
        }
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    // Modal focus management (initial focus, Tab/Shift+Tab trap, focus restore) via the
    // shared hook. Escape stays local because it defers to the suburb suggestions first.
    useFocusTrap({ active: isOpen, containerRef: panelRef, initialFocusRef: closeButtonRef });

    const handlePanelKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            if (showSuburbSuggestions) { setShowSuburbSuggestions(false); return; }
            onClose();
        }
    };

    /**
     * L2 — may this search be applied? Three ways a suburb is legitimate:
     *   1. the field is EMPTY (a filters-only apply, unchanged behaviour);
     *   2. it still shows the SEED — the suburb the visitor is already on, which the
     *      platform resolved for them, so re-picking it would be busywork;
     *   3. it was PICKED this session from the dropdown or from "use current location".
     * Anything else is free-typed text, which is the path that produced the unbounded
     * silent wait. ⚠️ Mirrors handleApply's own `usedSeed` derivation deliberately —
     * if these two ever disagree, the button and the action disagree.
     */
    const trimmedSuburb = suburbInput.trim();
    const seedSuburb = (locationDisplay || initialFilters.suburb || '').trim();
    const suburbMatchesSeed = !!seedSuburb && trimmedSuburb === seedSuburb;
    const suburbReady = !trimmedSuburb || suburbMatchesSeed || suburbPicked;

    const handleApply = () => {
        // Belt and braces: the button is disabled below, but Enter-to-submit or a future
        // caller must not be able to route around the rule.
        if (!suburbReady) return;
        // UI-130 (RULED B1): acknowledge the press. Set AFTER the guard above so a
        // blocked apply cannot disable the button it just refused to act on.
        setIsApplyPending(true);
        const trimmed = suburbInput.trim();
        // Removed local validation against NSW_LOCATIONS to reduce bundle size.
        // The backend handles location resolution.

        // If the location field was seeded from the resolved "Suburb Postcode" and the
        // user didn't edit it, apply the ORIGINAL suburb so we don't change the search
        // key — which would fire a redundant re-search + resolveLocation round-trip on
        // a Google-entry page. The pill still shows the postcode via displaySuburb.
        const usedSeed = !!locationDisplay && trimmed === locationDisplay.trim();
        const suburb = usedSeed ? initialFilters.suburb : trimmed;

        // Optimistic "Suburb Postcode" for the directory pill: the unedited seed, or the
        // last picked suggestion when the field still shows it. Undefined for a free-typed
        // suburb — there the search round-trip fills the postcode in.
        const resolvedDisplay = usedSeed
            ? (locationDisplay || undefined)
            : (selectedDisplayRef.current && /\b\d{4}\b/.test(selectedDisplayRef.current)
                ? selectedDisplayRef.current
                : undefined);

        // UI-121: THE CHOKE POINT. Every apply passes through here, so the id/suburb
        // pairing is re-checked once, in one place, rather than trusted from whichever
        // handler last ran. An id survives only if it still identifies the suburb being
        // applied; otherwise it is dropped and the search resolves the suburb normally.
        // ⚠️ Compared on TRIMMED text because `suburb` above is `suburbInput.trim()` on
        // the edited path while the ref is set from the untrimmed display string.
        const entryIdOwnsSuburb =
            entryIdSuburbRef.current.trim() === suburb.trim() && suburb.trim() !== '';
        const updatedFilters = {
            ...localFilters,
            suburb,
            ...(entryIdOwnsSuburb
                ? {}
                : { location_entry_id: null, location_source: null }),
        };
        setLocalFilters(updatedFilters);
        onApply(updatedFilters, resolvedDisplay, resolvedStateRef.current);
        onClose();
    };

    const handleClearAll = () => {
        const clearedFilters = {
            styles: [],
            ageGroups: [],
            experienceLevels: [],
            suburb: '',
            radius: 10,
            useRadius: false,
            gymName: ''
        };
        setLocalFilters(clearedFilters);
        setSuburbInput('');
        setSuburbPicked(false); // L2
        entryIdSuburbRef.current = ''; // UI-121: no suburb, so nothing owns an id
        onClearAll();
    };

    // --- Autocomplete Logic ---
    useEffect(() => {
        if (suburbInput.trim().length < 1) {
            setSuburbSuggestions([]);
            setSuburbActiveIndex(-1);
            setIsSearchingSuburbs(false);
            return;
        }

        let isCancelled = false;
        // The debounce window counts as searching: this is the exact interval in which
        // an empty suggestion list means "not yet", not "nothing matches".
        setIsSearchingSuburbs(true);

        const timer = setTimeout(async () => {
            try {
                const suggestions = await getLocationSuggestions(suburbInput, routeStateSlugRef.current);
                if (isCancelled) return;

                const isUnavailable = suggestions.some(s => s.display === "INDEX_UNAVAILABLE_MARKER");

                if (isUnavailable) {
                    setIndexUnavailable(true);
                    setSuburbSuggestions([]);
                } else {
                    setIndexUnavailable(false);
                    setSuburbSuggestions(suggestions);
                }
                setSuburbActiveIndex(-1);
                setIsSearchingSuburbs(false);
            } catch (error) {
                if (!isCancelled) {
                    setIsSearchingSuburbs(false);
                    console.error('[FilterModal] Suggestions failed:', error);
                }
            }
        }, 200);

        return () => {
            isCancelled = true;
            clearTimeout(timer);
        };
    }, [suburbInput]);

    // --- Suburb Logic ---
    const handleSuburbChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSuburbInput(value);
        // UI-121: the id is cleared in the SAME update as the suburb, not a second one —
        // a typed suburb has no known entry, and a surviving id would drop this suburb
        // out of the search key entirely. Clearing it in the draft (as well as at apply)
        // keeps the in-panel state from ever being internally inconsistent.
        setLocalFilters(prev => ({ ...prev, suburb: value, location_entry_id: null, location_source: null }));
        selectedDisplayRef.current = ''; // free typing invalidates the last picked postcode
        setSuburbPicked(false);          // ...and un-validates the suburb (L2)
        resolvedStateRef.current = undefined; // ...and invalidates the geolocated state
        entryIdSuburbRef.current = '';   // ...and un-owns the entry id (UI-121)
        setShowSuburbSuggestions(true);
    };

    const handleUseLocation = () => {
        if (!navigator.geolocation) {
            toast.error("Geolocation is not supported by your browser");
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                // ⚠️ UI-130 (RULED B2) — THE SPINNER STARTS HERE, NOT AT THE CLICK, AND THE
                // PLACEMENT IS THE RULING. `getCurrentPosition` fires the browser's own
                // permission prompt AND the lookup from one call; B2 covers the LOOKUP
                // only, because the prompt is the browser's modal and needs nothing from
                // us. Setting it inside the success callback means nothing spins behind
                // that prompt, and a denied permission leaves no spinner to clean up
                // (the error branch below is never reached with this true).
                setIsLocating(true);
                try {
                // Resolve to the nearest NSW locality via the GNAF index (item 16)
                // — replaces the previous hardcoded "Sydney 2000" placeholder,
                // which ignored the user's actual position.
                const nearest = await resolveNearestLocation(latitude, longitude);
                // Fallback example is PER-STATE (operator-ruled 2026-08-05, same pattern as
                // the header search label): a Victorian on a VIC page whose lookup fails
                // sees a Melbourne example, not a Sydney one. Hardcoded pair by design —
                // extend at the next state launch (the label test pattern will not catch
                // this one; it is a failure-path example, not a claim).
                const FALLBACK_EXAMPLE: Record<string, string> = { nsw: "Sydney 2000", vic: "Melbourne 3000" };
                const display = nearest?.display || FALLBACK_EXAMPLE[routeStateSlug] || "Sydney 2000";
                // Carry the RESOLVED state, not the route's. Absent when the lookup
                // failed and the per-state example was used — in which case the example
                // already belongs to the route's state, so undefined is correct.
                resolvedStateRef.current = nearest?.stateSlug;
                setSuburbInput(display);
                // Normalise "Suburb STATE Postcode" → "Suburb Postcode" so the seed matches
                // the postcode-less suburb the directory derives from the URL slug.
                selectedDisplayRef.current = /\b\d{4}\b/.test(display) ? normalizeLocationDisplay(display) : '';
                // L2: geolocation is a VALIDATED choice even when no postcode came back,
                // so it sets picked independently of the ref's postcode-conditional above.
                setSuburbPicked(true);
                // UI-121: geolocation SETS the correct id rather than merely clearing the
                // stale one. ⭐ `resolveNearestLocation` has always returned an `entryId`
                // and this handler threw it away — the same shape as the `stateSlug` it
                // used to discard (see the ITEM 6 note on resolvedStateRef). Setting it
                // makes this path agree with the two suggestion picks, and it also skips
                // the `resolveLocation` round-trip the search would otherwise run, which
                // is audit L2's dead window arriving on the one path L2's own fix leaves
                // open. ⚠️ Null when the lookup FAILED and the per-state example was used:
                // there is no entry behind "Sydney 2000", and a wrong id is worse than none.
                entryIdSuburbRef.current = nearest?.entryId ? display : '';
                setLocalFilters(prev => ({
                    ...prev,
                    suburb: display,
                    location_entry_id: nearest?.entryId || null,
                    // No `source` on the nearest-location wire shape — recorded as null
                    // rather than invented; nothing in the search path reads it.
                    location_source: null,
                }));
                setShowSuburbSuggestions(false);
                setSuburbActiveIndex(-1);
                } catch (err) {
                    // ⚠️ MINIMAL AND DELIBERATE. `resolveNearestLocation` had no error
                    // handling on this path, so a rejection escaped the async callback as
                    // an UNHANDLED promise rejection. Logging matches the geolocation
                    // error callback directly below, so this adds no new behaviour and no
                    // new copy. ⚠️ It is deliberately NOT surfaced to the visitor: RULED
                    // F1/F3 place error states outside this spec, and inventing a message
                    // here would pre-empt a decision that is the operator's to make.
                    console.error("Error resolving nearest location", err);
                } finally {
                    // ⚠️ `finally`, not a plain call after the await: `resolveNearestLocation`
                    // had NO error handling on this path, so a rejection already broke the
                    // handler silently — and it would now also strand the spinner on
                    // forever. This clears it either way. The rejection itself is still not
                    // surfaced to the visitor; that is an ERROR-state concern and RULED F1/F3
                    // put those outside this spec, so it is named here rather than widened into.
                    setIsLocating(false);
                }
            },
            (error) => {
                console.error("Error getting location", error);
                toast.error("Unable to retrieve your location. Please enter manually.");
            }
        );
    };

    const handleSuburbKeyDown = (e: React.KeyboardEvent) => {
        if (!showSuburbSuggestions) return;

        const totalSuggestions = suburbSuggestions.length + 1; // +1 for "Current location"

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSuburbActiveIndex(prev => (prev + 1) % totalSuggestions);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSuburbActiveIndex(prev => (prev - 1 + totalSuggestions) % totalSuggestions);
        } else if (e.key === 'Enter') {
            if (suburbActiveIndex === 0) {
                handleUseLocation();
            } else if (suburbActiveIndex > 0) {
                const selectedSub = suburbSuggestions[suburbActiveIndex - 1]!; // safe: suburbActiveIndex is clamped to the suggestions list by the keyboard handler
                setSuburbInput(selectedSub.display ?? '');
                selectedDisplayRef.current = toResolvedDisplay(selectedSub);
                setSuburbPicked(true); // L2
                entryIdSuburbRef.current = selectedSub.entryId ? (selectedSub.display ?? '') : ''; // UI-121
                setLocalFilters(prev => ({
                    ...prev,
                    suburb: selectedSub.display ?? '',
                    location_entry_id: selectedSub.entryId || null,
                    location_source: selectedSub.source || null
                }));
                setShowSuburbSuggestions(false);
            }
        } else if (e.key === 'Escape') {
            setShowSuburbSuggestions(false);
        }
    };

    // Scroll active item into view
    useEffect(() => {
        if (suburbActiveIndex >= 0 && suburbListRef.current) {
            const activeElement = suburbListRef.current.children[suburbActiveIndex] as HTMLElement;
            if (activeElement) {
                activeElement.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [suburbActiveIndex]);

    // --- Toggle Helpers ---
    const toggleStyle = (style: string) => {
        setLocalFilters(prev => {
            const exists = prev.styles.includes(style);
            return {
                ...prev,
                styles: exists
                    ? prev.styles.filter(s => s !== style)
                    : [...prev.styles, style]
            };
        });
    };

    const clearStyles = () => {
        setLocalFilters(prev => ({ ...prev, styles: [] }));
    };

    const toggleAgeGroup = (age: string) => {
        setLocalFilters(prev => {
            const exists = prev.ageGroups.includes(age);
            return {
                ...prev,
                ageGroups: exists
                    ? prev.ageGroups.filter(a => a !== age)
                    : [...prev.ageGroups, age]
            };
        });
    };

    const clearAgeGroups = () => {
        setLocalFilters(prev => ({ ...prev, ageGroups: [] }));
    };

    const toggleExperienceLevel = (exp: string) => {
        setLocalFilters(prev => ({
            ...prev,
            experienceLevels: prev.experienceLevels.includes(exp)
                ? prev.experienceLevels.filter(e => e !== exp)
                : [...prev.experienceLevels, exp]
        }));
    };

    if (!isOpen) return null;

    // UI-54 (operator-ruled 2026-08-11) — FULL viewport at EVERY breakpoint, rendered
    // through a portal to document.body.
    //
    // ⚠️ THE PREVIOUS COMMENT HERE WAS FALSE AND IS REPLACED. It claimed the `lg:top-0`
    // container made `aria-modal="true"` "true in fact (backdrop over the header,
    // nothing interactive outside the dialog)". It did not. This dialog was rendered
    // inside DirectoryPage's `fixed … z-10` container, which creates a STACKING CONTEXT
    // — so `z-[1100]` only ordered this dialog against its siblings INSIDE the
    // directory, and the whole directory lost to the header's `z-50` as one unit. At
    // lg+ the container spanned the viewport but the header still painted over its top
    // 64px; below lg the backdrop also STARTED 64px down, leaving the logo, the search
    // control and the hamburger lit and tappable — and the hamburger opens the nav
    // drawer (a root-context sibling at z-[60]/z-[70]) straight over this "modal".
    //
    // Geometry alone could not fix it: widening the backdrop leaves the paint order
    // wrong. The portal is what makes the modality claim true, so `inset-0` is now
    // correct at every width and the `lg:` exception is gone.
    //
    // ⚠️ The panel's max-height below is measured against THIS container. It was
    // `max-h-[90vh]` — the `-4rem` existing only to
    // compensate for the 64px offset that no longer exists — so it simplifies to a
    // single `max-h-[90vh]`. Do not reintroduce the subtraction without restoring the
    // offset, or the Clear All / Show Results footer clips with no scrollable ancestor.
    return (
        <ModalPortal>
        <div className="fixed inset-0 z-[1100] overflow-hidden" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            {/* Item 10: mounted at the DIALOG ROOT, outside the suggestions panel, so the
                live region exists before any outage message does. The visible notice
                inside the panel is unchanged; this is the audible half. */}
            <LiveAnnouncer message={indexUnavailable ? 'Location search is temporarily unavailable. Try again shortly.' : ''} />
            <div className="absolute inset-0 overflow-hidden">
                {/* Backdrop */}
                <div
                    className="absolute inset-0 transition-opacity bg-surface-ink/75"
                    onClick={onClose}
                    aria-hidden="true"
                    style={{
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)'
                    }}
                ></div>

                {/* Centered Modal Container */}
                <div className="flex items-center justify-center min-h-full p-4 text-center sm:p-0">

                    {/* Modal Panel */}
                    <div ref={panelRef} onKeyDown={handlePanelKeyDown} className="relative transform overflow-hidden rounded-2xl bg-white text-left transition-all sm:my-8 sm:w-full sm:max-w-lg md:max-w-3xl max-h-[90vh] flex flex-col" style={{ fontFamily: 'Inter, sans-serif' }}>

                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-3 border-b border-border-default flex-shrink-0 bg-white">
                            <h2 className="text-xl font-semibold text-text-primary" id="modal-title">Filters</h2>
                            {/* UI-13: panel close kept as raw <button> rather than IconButton —
                                house convention is a tight ~36px X with text-shift hover; the
                                primitive's 40px+ min-size and bg-fill hover would dominate the
                                header (same ruling as DirectoryPage's save-search modal close). */}
                            <button
                                ref={closeButtonRef}
                                aria-label="Close panel"
                                onClick={onClose}
                                className="p-2 text-text-subtle hover:text-text-primary transition-colors"
                            >
                                <X size={20} aria-hidden="true" />
                            </button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6 bg-white">

                            {/* Location & Radius Row - Stacked on mobile, side-by-side on tablet+ */}
                            <div className="flex flex-col gap-6 mb-6 md:grid md:grid-cols-2">
                                {/* Location (Left Column) */}
                                <div ref={suburbWrapperRef}>
                                    <LabelText className="text-sm font-medium text-text-primary uppercase tracking-wide mb-3 block">Location</LabelText>
                                    <div className="relative">
                                        <Input
                                            type="text"
                                            role="combobox"
                                            aria-expanded={showSuburbSuggestions}
                                            aria-controls="filter-suburb-listbox"
                                            aria-autocomplete="list"
                                            aria-activedescendant={suburbActiveIndex >= 0 ? `filter-suburb-option-${suburbActiveIndex}` : undefined}
                                            className="pl-10 text-left focus-visible:ring-0"
                                            placeholder="Suburb or Postcode"
                                            value={suburbInput}
                                            onChange={handleSuburbChange}
                                            onFocus={() => {
                                                setShowSuburbSuggestions(true);
                                                setSuburbActiveIndex(-1);
                                            }}
                                            onKeyDown={handleSuburbKeyDown}
                                            autoComplete="off"
                                        />
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                                            <MapPin className="h-5 w-5 text-text-subtle" />
                                        </div>
                                        {showSuburbSuggestions && (
                                            <div className="absolute z-50 mt-2 max-h-60 w-full overflow-hidden rounded-xl bg-white text-base ring-1 ring-border-default focus:outline-none sm:text-sm shadow-lg">
                                                {indexUnavailable && (
                                                    <div className="px-4 py-3 text-xs text-text-muted bg-slate-50 border-b border-border-default">
                                                        Location search is temporarily unavailable. Try again shortly.
                                                    </div>
                                                )}
                                                {/* UI-124, closed into UI-130 by operator ruling: B1 DOES reach the
                                                    autocomplete. Wording and shape deliberately mirror the header gym
                                                    search's empty state (`components/HeaderSearch.tsx`), which is the
                                                    platform's one exemplary loading affordance — the point of the
                                                    ruling was that the two autocompletes stop behaving differently.
                                                    ⚠️ Shown only when there is nothing else to show: while stale
                                                    suggestions are still on screen mid-debounce they stay visible,
                                                    matching the header's behaviour rather than blanking the list.
                                                    ⚠️ OUTSIDE the <ul> on purpose — a non-option child inside a
                                                    role="listbox" would be announced as a broken option. */}
                                                {isSearchingSuburbs && suburbSuggestions.length === 0 && (
                                                    <div className="px-4 py-3 text-sm text-text-muted flex items-center gap-2 border-b border-border-subtle">
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                        Searching suburbs…
                                                    </div>
                                                )}
                                                <ul ref={suburbListRef} id="filter-suburb-listbox" role="listbox" aria-label="Location suggestions" className="max-h-48 overflow-auto focus:outline-none custom-scrollbar">
                                                    {/* Current Location Option */}
                                                    <li
                                                        id="filter-suburb-option-0"
                                                        role="option"
                                                        aria-selected={suburbActiveIndex === 0}
                                                        onClick={handleUseLocation}
                                                        className={`cursor-pointer select-none relative py-3 pl-4 pr-9 transition-colors flex items-center group ${suburbSuggestions.length > 0 ? 'border-b border-border-subtle' : ''} ${suburbActiveIndex === 0 ? 'bg-brand-primary-subtle' : 'hover:bg-surface-canvas'}`}
                                                    >
                                                        {/* UI-130 (RULED B2) — the platform's last silent wait. This is
                                                            a bespoke <li role="option">, NOT the shared <Button>, so
                                                            there is no `isLoading` capability to switch on and the
                                                            spinner is hand-built. Icon swaps in place and the label
                                                            changes, because on a phone the 16px icon alone was judged
                                                            too subtle to read as feedback — which is the very defect
                                                            being fixed. Copy ruled by the operator. */}
                                                        <div className={`flex-shrink-0 mr-3 rounded-full p-2 transition-colors ${suburbActiveIndex === 0 ? 'bg-brand-primary-subtle group-hover:bg-brand-primary-subtle' : 'bg-brand-primary-subtle group-hover:bg-brand-primary-subtle'}`}>
                                                            {isLocating
                                                                ? <Loader2 size={16} className="text-brand-primary animate-spin" />
                                                                : <Navigation size={16} fill="currentColor" className="text-brand-primary" />}
                                                        </div>
                                                        <span className="block truncate font-semibold text-text-primary">
                                                            {isLocating ? 'Finding your location…' : 'Current location'}
                                                        </span>
                                                    </li>

                                                    {/* Suggestions */}
                                                    {suburbSuggestions.map((sub, index) => (
                                                        <li
                                                            key={sub.display + '-' + index}
                                                            id={`filter-suburb-option-${index + 1}`}
                                                            role="option"
                                                            aria-selected={suburbActiveIndex === index + 1}
                                                            className={`relative cursor-pointer select-none py-3 pl-4 pr-9 transition-colors flex items-center ${suburbActiveIndex === index + 1 ? 'bg-brand-primary-subtle text-brand-primary' : 'text-text-primary hover:bg-brand-primary-subtle hover:text-brand-primary'}`}
                                                            onClick={() => {
                                                                setSuburbInput(sub.display ?? '');
                                                                selectedDisplayRef.current = toResolvedDisplay(sub);
                                                                setSuburbPicked(true); // L2
                                                                entryIdSuburbRef.current = sub.entryId ? (sub.display ?? '') : ''; // UI-121
                                                                setLocalFilters(prev => ({
                                                                    ...prev,
                                                                    suburb: sub.display ?? '',
                                                                    location_entry_id: sub.entryId || null,
                                                                    location_source: sub.source || null
                                                                }));
                                                                setShowSuburbSuggestions(false);
                                                            }}
                                                        >
                                                            <div className="flex-shrink-0 mr-3 bg-surface-raised text-text-muted rounded-full p-2">
                                                                <MapPin size={16} />
                                                            </div>
                                                            <span className="block truncate font-medium">{sub.display}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                    {/* ⚠️ L2 — A DISABLED CONTROL WITH NO STATED REASON IS ITS OWN DEAD END.
                                        Shown only while the field holds text that was never picked, so it
                                        appears exactly when Show Results is unavailable and explains why.
                                        `aria-live` so a screen reader hears it, since the disabled button
                                        gives no announcement of its own.
                                        ⚠️ COPY ROUTES TO THE OPERATOR — this wording is functional
                                        placeholder text chosen to state the rule, not approved copy. */}
                                    {!suburbReady && !isSearchingSuburbs && !indexUnavailable && (
                                        <p role="status" aria-live="polite" className="mt-2 text-sm text-text-muted">
                                            {suburbSuggestions.length > 0
                                                ? 'Choose a suburb from the list to search.'
                                                : 'No matching suburb. Check the spelling or use your current location.'}
                                        </p>
                                    )}
                                </div>

                                {/* Search Radius Toggle + Slider (Right Column) */}
                                <div>
                                    <div className="flex justify-between text-sm font-medium text-text-primary mb-3 items-center">
                                        <span className="uppercase tracking-wide">Search Radius</span>
                                        {/* Smaller Toggle Switch - inline with title */}
                                        <div
                                            role="switch"
                                            tabIndex={0}
                                            aria-checked={localFilters.useRadius}
                                            onClick={() => setLocalFilters(prev => ({ ...prev, useRadius: !prev.useRadius }))}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    setLocalFilters(prev => ({ ...prev, useRadius: !prev.useRadius }));
                                                }
                                            }}
                                            className={`
                                                relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                                                transition-colors duration-200 ease-in-out focus:outline-none focus:ring-1 focus:ring-inset focus:ring-brand-primary focus:ring-offset-0 focus:border-brand-primary
                                                ${localFilters.useRadius ? 'bg-brand-primary' : 'bg-border-default'}
                                            `}
                                        >
                                            <span className="sr-only">Enable radius search</span>
                                            <span
                                                aria-hidden="true"
                                                className={`
                                                    pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0
                                                    transition duration-200 ease-in-out
                                                    ${localFilters.useRadius ? 'translate-x-4' : 'translate-x-0'}
                                                `}
                                            />
                                        </div>
                                    </div>

                                    {/* Slider area - always rendered but hidden when toggle is off to maintain consistent height */}
                                    <div className={`transition-opacity duration-200 ${localFilters.useRadius ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                                        <input
                                            type="range"
                                            min="1"
                                            max="20"
                                            step="1"
                                            className="w-full h-2 bg-border-default rounded-lg appearance-none cursor-pointer accent-brand-primary"
                                            value={localFilters.radius}
                                            onChange={(e) => setLocalFilters({ ...localFilters, radius: parseInt(e.target.value) })}
                                            tabIndex={localFilters.useRadius ? 0 : -1}
                                        />
                                        <div className="flex justify-between text-xs text-text-muted mt-2 font-medium">
                                            <span>1km</span>
                                            <span className="bg-brand-primary/10 text-brand-primary px-2 py-0.5 rounded-md">{localFilters.radius}km</span>
                                            <span>20km</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Filters Section */}
                            <div>
                                {/* Martial Art Styles — fieldset/legend + role=checkbox pills so the
                                    multi-select state is conveyed semantically, not by colour alone (D3). */}
                                <fieldset className="mb-6 m-0 p-0 border-0 min-w-0">
                                    <legend className="text-sm font-medium text-text-primary uppercase tracking-wide mb-3 block">Martial Art Style</legend>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                        {/* Any Style Option — checked when no specific style is selected */}
                                        <Button
                                            intent="outline"
                                            role="checkbox"
                                            aria-checked={localFilters.styles.length === 0}
                                            onClick={clearStyles}
                                            className={`
                                                px-3 py-2 rounded-md text-sm font-medium border transition-all text-left flex justify-between items-center h-auto
                                                ${localFilters.styles.length === 0
                                                    ? 'bg-brand-primary/10 border-brand-primary text-brand-primary'
                                                    : 'bg-surface-canvas text-text-primary border-border-default hover:border-text-subtle'
                                                }
                                            `}
                                        >
                                            <span className="truncate mr-2">Any Style</span>
                                        </Button>

                                        {/* Specific Styles */}
                                        {STYLE_OPTIONS.map(style => {
                                            const isSelected = localFilters.styles.includes(style);
                                            return (
                                                <Button
                                                    intent="outline"
                                                    key={style}
                                                    role="checkbox"
                                                    aria-checked={isSelected}
                                                    onClick={() => toggleStyle(style)}
                                                    className={`
                                                        px-3 py-2 rounded-md text-sm font-medium border transition-all text-left flex justify-between items-center h-auto
                                                        ${isSelected
                                                            ? 'bg-brand-primary/10 border-brand-primary text-brand-primary'
                                                            : 'bg-surface-canvas text-text-primary border-border-default hover:border-text-subtle'
                                                        }
                                                    `}
                                                >
                                                    <span className="truncate mr-2">{style}</span>
                                                </Button>
                                            );
                                        })}
                                    </div>
                                </fieldset>

                                {/* Age Group (D3: fieldset/legend + role=checkbox pills) */}
                                <fieldset className="mb-6 m-0 p-0 border-0 min-w-0">
                                    <legend className="text-sm font-medium text-text-primary uppercase tracking-wide mb-3 block">Age Group</legend>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                        {/* Any Age Option — checked when no specific age is selected */}
                                        <Button
                                            intent="outline"
                                            role="checkbox"
                                            aria-checked={localFilters.ageGroups.length === 0}
                                            onClick={clearAgeGroups}
                                            className={`
                                                px-3 py-2 rounded-md text-sm font-medium border transition-all text-left flex justify-between items-center h-auto
                                                ${localFilters.ageGroups.length === 0
                                                    ? 'bg-brand-primary/10 border-brand-primary text-brand-primary'
                                                    : 'bg-surface-canvas text-text-primary border-border-default hover:border-text-subtle'
                                                }
                                            `}
                                        >
                                            <span className="truncate mr-2">Any Age</span>
                                        </Button>

                                        {/* Specific Age Groups */}
                                        {AGE_OPTIONS.map(age => {
                                            const isSelected = localFilters.ageGroups.includes(age);
                                            return (
                                                <Button
                                                    intent="outline"
                                                    key={age}
                                                    role="checkbox"
                                                    aria-checked={isSelected}
                                                    onClick={() => toggleAgeGroup(age)}
                                                    className={`
                                                        px-3 py-2 rounded-md text-sm font-medium border transition-all text-left flex justify-between items-center h-auto
                                                        ${isSelected
                                                            ? 'bg-brand-primary/10 border-brand-primary text-brand-primary'
                                                            : 'bg-surface-canvas text-text-primary border-border-default hover:border-text-subtle'
                                                        }
                                                    `}
                                                >
                                                    <span className="truncate mr-2">{age}</span>
                                                </Button>
                                            );
                                        })}
                                    </div>
                                </fieldset>

                                {/* Experience Level (D3: fieldset/legend + role=checkbox pills) */}
                                <fieldset className="mb-4 m-0 p-0 border-0 min-w-0">
                                    <legend className="text-sm font-medium text-text-primary uppercase tracking-wide mb-3 block">Experience Level</legend>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                        {/* Any Experience Option — checked when no specific level is selected */}
                                        <Button
                                            intent="outline"
                                            role="checkbox"
                                            aria-checked={localFilters.experienceLevels.length === 0}
                                            onClick={() => setLocalFilters(prev => ({ ...prev, experienceLevels: [] }))}
                                            className={`
                                                px-3 py-2 rounded-md text-sm font-medium border transition-all text-left flex justify-between items-center h-auto
                                                ${localFilters.experienceLevels.length === 0
                                                    ? 'bg-brand-primary/10 border-brand-primary text-brand-primary'
                                                    : 'bg-surface-canvas text-text-primary border-border-default hover:border-text-subtle'
                                                }
                                            `}
                                        >
                                            <span className="truncate mr-2">Any Level</span>
                                        </Button>

                                        {/* Specific Experience Levels */}
                                        {EXPERIENCE_OPTIONS.map(exp => {
                                            const isSelected = localFilters.experienceLevels.includes(exp);
                                            return (
                                                <Button
                                                    intent="outline"
                                                    key={exp}
                                                    role="checkbox"
                                                    aria-checked={isSelected}
                                                    onClick={() => toggleExperienceLevel(exp)}
                                                    className={`
                                                        px-3 py-2 rounded-md text-sm font-medium border transition-all text-left flex justify-between items-center h-auto
                                                        ${isSelected
                                                            ? 'bg-brand-primary/10 border-brand-primary text-brand-primary'
                                                            : 'bg-surface-canvas text-text-primary border-border-default hover:border-text-subtle'
                                                        }
                                                    `}
                                                >
                                                    <span className="truncate mr-2">{exp}</span>
                                                </Button>
                                            );
                                        })}
                                    </div>
                                </fieldset>
                            </div>

                        </div>

                        {/* Footer */}
                        <div className="px-6 py-5 bg-white border-t border-border-default flex-shrink-0">
                            <div className="flex gap-3">
                                <Button
                                    intent="ghost"
                                    className="flex-1 bg-surface-canvas hover:bg-border-default"
                                    onClick={handleClearAll}
                                >
                                    Clear All
                                </Button>
                                {/* UI-130 (RULED B1): `isLoading` both spins and disables —
                                    the primitive ORs it with `disabled`, so the existing
                                    suburbReady rule still holds. Like the hero, the spinner
                                    is usually unseen because `handleApply` closes the modal
                                    on the same tick; the load-bearing half is that a second
                                    click cannot fire a second apply. */}
                                <Button
                                    intent="primary"
                                    className="flex-[2]"
                                    onClick={handleApply}
                                    isLoading={isApplyPending}
                                    disabled={!suburbReady}
                                >
                                    Show Results
                                </Button>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
        </ModalPortal>
    );
};

export default FilterModal;
